import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Copy, Server, Edit2 } from "lucide-react";
import { rpcApi, RPCResponse } from "@/api/rpc";
import { useAppSelector } from "@/store/hooks";
import { toast as sonnerToast } from "sonner";
import { UserMeData } from "@/types/profile";

interface FTPSettingsProps {
  userId?: string;
  userEmail?: string;
  ftpData?: {
    status?: number;
    password?: string;
    hasPassword?: boolean;
  };
}

export default function FTPSettings({ userId, userEmail, ftpData: initialFtpData }: FTPSettingsProps) {
  const { toast } = useToast();
  const authState = useAppSelector((state) => state.auth);
  
  const currentUserEmail = userEmail || authState.userEmail || "";

  const [showPassword, setShowPassword] = useState(false);
  const [ftpStatus, setFtpStatus] = useState<boolean>(false);
  const [ftpPassword, setFtpPassword] = useState<string>("");
  const [actualFtpPassword, setActualFtpPassword] = useState<string>("");
  const [hasPassword, setHasPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showDisableConfirmDialog, setShowDisableConfirmDialog] = useState(false);
  const [actualUserId, setActualUserId] = useState<string | null>(userId || null);
  
  useEffect(() => {
    if (userId) {
      setActualUserId(userId);
    }
  }, [userId]);
  const [pendingEnable, setPendingEnable] = useState<boolean>(false);
  const [passwordError, setPasswordError] = useState<string>("");
  const [showPasswordError, setShowPasswordError] = useState<boolean>(false);

  const MASKED_PASSWORD = "******";

  const getErrorMessage = (error: unknown, fallback: string): string => {
    if (typeof error === "string") return error;
    if (error && typeof error === "object") {
      if ("message" in error && typeof error.message === "string") return error.message;
      if ("error" in error) {
        const errObj = error.error;
        if (typeof errObj === "string") return errObj;
        if (errObj && typeof errObj === "object" && "message" in errObj && typeof errObj.message === "string") {
          return errObj.message;
        }
      }
    }
    return fallback;
  };

  const initializeFtpState = (ftpData: { status?: number; password?: string; hasPassword?: boolean } | undefined) => {
    if (!ftpData) {
      setFtpStatus(false);
      setHasPassword(false);
      setActualFtpPassword("");
      setFtpPassword("");
      return;
    }

    const isEnabled = ftpData.status === 1;
    const passwordExists = !!(ftpData.hasPassword || (ftpData.password && ftpData.password.trim() !== ""));
    
    setFtpStatus(isEnabled);
    setHasPassword(passwordExists);
    
    if (isEnabled && passwordExists) {
      setActualFtpPassword(ftpData.password || "");
      setFtpPassword(MASKED_PASSWORD);
    } else {
      setActualFtpPassword("");
      setFtpPassword(isEnabled ? MASKED_PASSWORD : "");
    }
  };

  const resetPasswordDialog = () => {
    setNewPassword("");
    setPasswordError("");
    setShowPasswordError(false);
    setPendingEnable(false);
  };

  const hasUserInfo = (): boolean => {
    return !!(actualUserId && currentUserEmail);
  };

  const showMissingUserInfoError = () => {
    toast({
      title: "Error",
      description: "Missing user information. Please refresh the page.",
      variant: "destructive",
    });
  };

  useEffect(() => {
    if (initialFtpData) {
      initializeFtpState(initialFtpData);
      return;
    }

    const fetchUserData = async () => {
      try {
        const userResponse = await rpcApi.getUserMe() as RPCResponse<UserMeData> & { data?: { result?: UserMeData } };
        const userData = userResponse?.result || userResponse?.data?.result;
        
        if (userData) {
          if (!actualUserId && userData.id) {
            setActualUserId(userData.id);
          }

          initializeFtpState(userData.ftp);
        }
      } catch (error) {
        // Silent fail
      }
    };

    fetchUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFtpData]);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to Clipboard",
      description: `${label} has been copied to your clipboard.`,
    });
  };

  const updateFTPStatus = async (status: number) => {
    if (!hasUserInfo()) {
      showMissingUserInfoError();
      return false;
    }

    setIsLoading(true);
    try {
      const response = await rpcApi.updateFTP(
        actualUserId,
        { status, email: currentUserEmail }
      );

      if (response?.result || response?.error === undefined) {
        setFtpStatus(status === 1);
        sonnerToast.success(`FTP ${status === 1 ? "enabled" : "disabled"} successfully`);
        return true;
      } else {
        const errorMessage = typeof response.error === 'string' 
          ? response.error 
          : response.error?.message || "Failed to update FTP status";
        throw new Error(errorMessage);
      }
    } catch (error: unknown) {
      sonnerToast.error(getErrorMessage(error, "Failed to update FTP status"));
      setFtpStatus(!(status === 1));
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (checked: boolean) => {
    if (!checked) {
      setShowDisableConfirmDialog(true);
      return;
    }

    if (!currentUserEmail) {
      toast({
        title: "Error",
        description: "Email is required to enable FTP.",
        variant: "destructive",
      });
      return;
    }

    if (!hasPassword) {
      toast({
        title: "Password Required",
        description: "Please set an FTP password before enabling FTP services.",
        variant: "default",
      });
      setPendingEnable(true);
      resetPasswordDialog();
      setShowPasswordDialog(true);
      return;
    }

    await updateFTPStatus(1);
  };

  const validatePassword = (password: string): string | null => {
    const passwordLength = password.trim().length;
    if (passwordLength < 3) {
      return "Password must be at least 3 characters long";
    }
    if (passwordLength > 20) {
      return "Password must be at most 20 characters long";
    }
    if (!password.trim()) {
      return "Please enter a new password";
    }
    return null;
  };

  const handleEditPassword = () => {
    resetPasswordDialog();
    setShowPasswordDialog(true);
  };

  const handleCancelPasswordEdit = () => {
    setShowPasswordDialog(false);
    resetPasswordDialog();
  };

  const handleUpdatePassword = async () => {
    setShowPasswordError(true);

    const validationError = validatePassword(newPassword);
    if (validationError) {
      setPasswordError(validationError);
      return;
    }

    setPasswordError("");

    if (!hasUserInfo()) {
      showMissingUserInfoError();
      return;
    }

    setIsLoading(true);
    try {
      const status = pendingEnable ? 1 : (ftpStatus ? 1 : 0);
      
      const response = await rpcApi.updateFTP(
        actualUserId,
        { status, password: newPassword, email: currentUserEmail }
      );

      if (response?.result || response?.error === undefined) {
        setActualFtpPassword("");
        setFtpPassword(MASKED_PASSWORD);
        setHasPassword(true);
        
        setShowPasswordDialog(false);
        setShowPassword(false);
        resetPasswordDialog();
        
        if (pendingEnable) {
          setFtpStatus(true);
          sonnerToast.success("FTP password set and FTP enabled successfully");
        } else {
          sonnerToast.success("FTP password updated successfully");
        }
      } else {
        const errorMessage = typeof response.error === 'string' 
          ? response.error 
          : response.error?.message || "Failed to update FTP password";
        throw new Error(errorMessage);
      }
    } catch (error: unknown) {
      sonnerToast.error(getErrorMessage(error, "Failed to update FTP password"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Server className="w-5 h-5 text-primary" />
              <CardTitle>FTP Login Settings</CardTitle>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">OFF</span>
              <Switch
                checked={ftpStatus}
                onCheckedChange={handleStatusChange}
                disabled={isLoading}
              />
              <span className="text-sm text-muted-foreground">ON</span>
            </div>
          </div>
          <CardDescription>
            Manage your FTP credentials for file transfers
          </CardDescription>
        </CardHeader>
        {ftpStatus && (
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ftp-username">FTP Username</Label>
              <div className="flex gap-2">
                <Input
                  id="ftp-username"
                  value={currentUserEmail}
                  readOnly
                  className="bg-muted/50"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleCopy(currentUserEmail, "FTP Username")}
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="ftp-password">FTP Password</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEditPassword}
                  className="gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Change Password
                </Button>
              </div>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="ftp-password"
                    type={showPassword ? "text" : "password"}
                    value={showPassword && actualFtpPassword ? actualFtpPassword : (ftpPassword || MASKED_PASSWORD)}
                    readOnly
                    className={`bg-muted/50 ${actualFtpPassword ? "pr-10" : ""}`}
                  />
                  {actualFtpPassword && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Use this password to access FTP services. Keep it secure.
              </p>
            </div>
          </CardContent>
        )}
      </Card>

      <Dialog 
        open={showPasswordDialog} 
        onOpenChange={(open) => {
          setShowPasswordDialog(open);
          if (!open) {
            resetPasswordDialog();
          }
        }}
      >
        <DialogContent>
            <DialogHeader>
            <DialogTitle>{!hasPassword || pendingEnable ? "Set FTP Password" : "Edit FTP Password"}</DialogTitle>
            <DialogDescription>
              {!hasPassword || pendingEnable
                ? "Please set a password for your FTP account to enable FTP services."
                : "Enter a new password for your FTP account. This will replace your current password."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-ftp-password">New FTP Password</Label>
              <Input
                id="new-ftp-password"
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  // Clear error when user starts typing
                  if (showPasswordError) {
                    setPasswordError("");
                  }
                }}
                placeholder="Enter new password"
                disabled={isLoading}
                className={showPasswordError && passwordError ? "border-destructive" : ""}
              />
              {showPasswordError && passwordError && (
                <p className="text-sm text-destructive">{passwordError}</p>
              )}
              {!showPasswordError && (
                <p className="text-xs text-muted-foreground">
                  Password must be between 3 and 20 characters
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={handleCancelPasswordEdit}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdatePassword}
              disabled={isLoading}
            >
              {isLoading 
                ? (!hasPassword || pendingEnable ? "Setting..." : "Updating...") 
                : (!hasPassword || pendingEnable ? "Set Password" : "Update")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDisableConfirmDialog} onOpenChange={setShowDisableConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable FTP Services?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disable FTP services? You will need to enable it again and may need to reconfigure your FTP settings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowDisableConfirmDialog(false);
              setFtpStatus(true);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                setShowDisableConfirmDialog(false);
                await updateFTPStatus(0);
              }}
            >
              Disable FTP
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
